import { useModal } from "@ebay/nice-modal-react";
import AddIcon from "@mui/icons-material/Add";
import { List } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import TypesenseInstantSearch, {
  TypesenseContext,
} from "components/context/TypesenseInstantSearch";
import useQueryStringState from "components/hooks/useQueryStringState";
import useInstantSearchConfig from "components/shared/useInstantSearchConfig";
import Button, {
  TypesenseInfiniteScrollButton,
} from "components/ui/atoms/Button";
import Highlight from "components/ui/atoms/Highlight";
import FilterAndSort from "components/ui/molecules/FilterAndSort";
import Layout from "components/ui/molecules/Layout";
import CompanyFilterForm from "models/companies/CompanyFiltersForm";
import CompanyListItem from "models/companies/CompanyListItem";
import CompanySortForm from "models/companies/CompanySortForm";
import { useContext, useEffect, useState } from "react";
import { useInfiniteHits } from "react-instantsearch";

const InnerCompany = ({ clearTypesensCache }) => {
  const theme = useTheme();
  const [state, setState] = useQueryStringState();
  const companyModal = useModal("CompanyDrawer");
  const { typesenseEnv } = useContext(TypesenseContext);
  const { applyState } = useInstantSearchConfig({
    sortBy: [{ value: `${typesenseEnv}_company`, label: "Company" }],
    refinements: [
      {
        refinementType: "list",
        refinementName: "deal_flows.name_facet",
        filterName: "deal_flows",
      },
      {
        refinementType: "numeric",
        refinementName: "reference_revenue_facet",
        filterNameMin: "reference_revenue_min",
        filterNameMax: "reference_revenue_max",
      },
      {
        refinementType: "list",
        refinementName: "deal_flows.max_investment_status_facet",
        filterName: "max_investment_status",
      },
      {
        refinementType: "list",
        refinementName: "sector_tags.value_facet",
        filterName: "sectors",
      },
      {
        refinementType: "list",
        refinementName: "business_model_tags.value_facet",
        filterName: "business_models",
      },
      {
        refinementType: "list",
        refinementName: "mapping_and_certification_tags.value_facet",
        filterName: "mapping_and_certifications",
      },
      {
        refinementType: "list",
        refinementName: "impact_model_tags.value_facet",
        filterName: "impact_models",
      },
      {
        refinementType: "list",
        refinementName: "deal_flows.investment_status_facet",
        filterName: "investment_status",
      },
      {
        refinementType: "list",
        refinementName: "investment_round_facet",
        filterName: "investment_rounds",
      },
      {
        refinementType: "toggle",
        refinementName: "profitable_facet",
        filterName: "profitable",
      },
      {
        refinementType: "numeric",
        refinementName: "requested_amount_facet",
        filterNameMin: "requested_amount_min",
        filterNameMax: "requested_amount_max",
      },
      {
        refinementType: "datenumeric",
        refinementName: "created_at_facet",
        filterNameMin: "created_at_min",
        filterNameMax: "created_at_max",
      },
      {
        refinementType: "numeric",
        refinementName: "last_fundraising_amount_facet",
        filterNameMin: "last_fundraising_amount_min",
        filterNameMax: "last_fundraising_amount_max",
      },
      {
        refinementType: "numeric",
        refinementName: "total_amount_raised_facet",
        filterNameMin: "total_amount_raised_min",
        filterNameMax: "total_amount_raised_max",
      },
      {
        refinementType: "list",
        refinementName: "headquarter_address.country_facet",
        filterName: "country",
      },
      {
        refinementType: "list",
        refinementName: "headquarter_address.administrative_area_level_1_facet",
        filterName: "area",
      },
      {
        refinementType: "list",
        refinementName: "headquarter_address.locality_facet",
        filterName: "locality",
      },
    ],
  });
  const { hits, results } = useInfiniteHits();

  useEffect(() => {
    applyState(state);
  }, [state]);

  return (
    <Layout container hideBreadcrumbs>
      <Layout title variant="component">
        <Grid container spacing={2} alignItems="center" sx={{ width: "100%" }}>
          <Grid>
            <Highlight color={theme.palette_custom.lightGreen}>
              Company
            </Highlight>
          </Grid>
          <Grid ml="auto">
            <Button
              endIcon={
                <AddIcon sx={{ transform: "rotate(90deg)", scale: "0.8" }} />
              }
              onClick={() =>
                companyModal
                  .show({ formType: "create" })
                  .then(() => clearTypesensCache)
              }
            >
              Create
            </Button>
          </Grid>
        </Grid>
      </Layout>
      <Layout content>
        <FilterAndSort
          FilterForm={CompanyFilterForm}
          SortForm={CompanySortForm}
          state={state}
          setState={setState}
          count={results?.nbHits}
        />
      </Layout>
      <Layout content>
        <List variant="boxed">
          {hits.map((hit) => (
            <CompanyListItem
              key={hit.id}
              company={hit}
              onClick={(companyId) =>
                companyModal.show({
                  formType: "update",
                  id: companyId,
                })
              }
            />
          ))}
          <TypesenseInfiniteScrollButton />
        </List>
      </Layout>
    </Layout>
  );
};

const Company = () => {
  const searchClient = useContext(TypesenseContext);
  const [key, setKey] = useState(0);

  //key is the only simple way to refresh infinitHits state contained in InnerNote
  const refresh = () => {
    searchClient.clearCache();
    setKey(key + 1);
  };

  return (
    <TypesenseInstantSearch indexName="company">
      <InnerCompany key={key} clearTypesensCache={refresh} />
    </TypesenseInstantSearch>
  );
};

export default Company;
